import { Injectable } from '@angular/core';
import { AccountsService } from '@epicuro-next/data/accounts';
import { MerchandiseService } from '@epicuro-next/data/merchandise';
import { APP_TOP_LEVEL_PATH } from '@epicuro-next/navigation/constants';
import { EnvironmentService } from '@epicuro-next/platform/environment';
import { registerSentryUserSiteId } from '@epicuro-next/platform/sentry';
import {
  clearActiveAccount,
  setActiveAccount,
} from '@epicuro-next/state/accounts-state';
import { go } from '@epicuro-next/state/router-store';
import {
  patchSession,
  selectAccountId,
  selectSiteId,
  selectSubSiteId,
  SITE_ID,
  SUB_SITE_ID,
} from '@epicuro-next/state/session-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Account, Site } from 'graphql-api/gql-api-interfaces';
import { EMPTY, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  clearSites,
  initSites,
  loadAllSitesSuccess,
  loadSiteSelectorBackgroundSuccess,
  loadSitesSuccess,
  preloadFailure,
  preloadSites,
  preloadSitesSuccess,
  setSelectedSite,
} from '../actions/sites.actions';
import { isCurrAndPrevSitesEqual, ISite, siteSliceUrlBuilder } from '../models';
import {
  selectCurrentAccountSites,
  selectPreLoadedSites,
} from '../selectors/sites.selectors';
import { loginSuccess } from '@epicuro-next/state/user-state';

const CLEAR_SITE_SELECTOR_FLOW_ACTIONS = [
  patchSession({ session: { [SITE_ID]: null, [SUB_SITE_ID]: null } }),
  setSelectedSite({ site: null, skipLocationChange: true }),
  go({
    commands: ['/', APP_TOP_LEVEL_PATH.siteSelector.path],
    extras: { state: { forceLeaveBasket: true } },
  }),
];

@Injectable()
export class SitesEffects {
  public onInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginSuccess),
      map(() => initSites()),
    ),
  );

  // Load background for site selector view
  public loadBackground$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initSites),
      withLatestFrom(this.environmentService.environmentSettings),
      filter(([, environment]) => JSON.parse(environment.multiSiteSupport)),
      switchMap(() =>
        this.store.pipe(
          select(selectAccountId),
          filter((v): v is string => !!v),
        ),
      ),
      switchMap((accountId) =>
        this.merchandiseService.getSiteSelectorBackground(accountId),
      ),
      map((background) => loadSiteSelectorBackgroundSuccess({ background })),
      catchError(() => EMPTY),
    ),
  );

  public loadSites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initSites),
      switchMap(() =>
        this.accountService
          .getSites()
          .pipe(map((sites) => loadAllSitesSuccess({ sites }))),
      ),
    ),
  );

  public getActiveAccountDetails$ = createEffect(() =>
    this.actions$
      .pipe(
        ofType(setActiveAccount),
        map(({ account }) => account),
        withLatestFrom(
          this.store.select(selectPreLoadedSites),
          this.store.select(selectCurrentAccountSites),
        ),
        switchMap(
          ([currAccount, preloaded, savedCurrentAccountSites]: [
            Account,
            { [accountId: string]: Site[] },
            Site[],
          ]) => {
            // if user account has sites
            if (!!currAccount?.sites) {
              return of(currAccount);
            }

            // previously uploaded (while user choosing account)
            if (preloaded[currAccount.accountId]?.length > 0) {
              return of({ sites: preloaded[currAccount?.accountId ?? ''] });
            }

            // previously saved in persistant storage
            // we can safily take from localStorage if account ids are
            // same
            if (savedCurrentAccountSites?.length > 0) {
              return of({ sites: savedCurrentAccountSites });
            }

            return this.accountService.getAccountDetails({
              accountId: currAccount.accountId,
            });
          },
        ),
      )
      .pipe(
        switchMap(({ sites }) => {
          if (sites != null && sites.length > 0) {
            return of({ sites: sites as Site[] });
          }

          return this.accountService.getSites().pipe(
            map((allSites: Site[]) => ({
              sites: allSites,
            })),
          );
        }),
        map(({ sites }) =>
          loadSitesSuccess({
            sites,
          }),
        ),
      ),
  );

  // Listen to sites change so we can verify that, e.g. different account,
  // has the same site as prev account had. If not, need to be redirected to
  // site selector and session regarding site selector cleared
  public accountSitesChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSitesSuccess),
      map(({ sites }) => sites),
      distinctUntilChanged((prevSites, currSites) => {
        return isCurrAndPrevSitesEqual(prevSites, currSites);
      }),
      withLatestFrom(
        this.store.pipe(
          select(selectSiteId),
          filter((v) => isNonNullish(v)),
        ),
        this.store.pipe(select(selectSubSiteId)),
      ),
      switchMap(([sites, siteId, subSiteId]) => {
        const foundSite = sites.find((site) => site.siteId === siteId);
        if (foundSite) {
          if (subSiteId) {
            const foundSubSite = foundSite.subSites?.find(
              (subSite) => subSite?.subSiteId === subSiteId,
            );
            if (foundSubSite) {
              return EMPTY;
            }

            return CLEAR_SITE_SELECTOR_FLOW_ACTIONS;
          }

          return EMPTY;
        }

        return CLEAR_SITE_SELECTOR_FLOW_ACTIONS;
      }),
    ),
  );

  public setSelectedSite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSelectedSite),
      filter(({ site }) => isNonNullish(site)),
      distinctUntilChanged(),
      switchMap(({ site, skipLocationChange }) => {
        const { siteId, subSiteId } = site as ISite;

        const setSelectedActions: Action[] = [
          patchSession({
            session: { [SITE_ID]: siteId, [SUB_SITE_ID]: subSiteId },
          }),
        ];

        if (!skipLocationChange) {
          setSelectedActions.push(
            go({
              commands: [
                '/',
                APP_TOP_LEVEL_PATH.siteSelector.custom,
                siteSliceUrlBuilder(siteId, subSiteId),
              ],
            }),
          );
        }

        return setSelectedActions;
      }),
    ),
  );

  public registerSentryUserSite$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setSelectedSite),
        tap(({ site }) => {
          if (site?.siteId) registerSentryUserSiteId(site?.siteId);
        }),
      ),
    { dispatch: false },
  );

  public preloadSites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(preloadSites),
      withLatestFrom(this.store.select(selectPreLoadedSites)),
      mergeMap(([{ accountId }, preloadedSites]) =>
        !preloadedSites[accountId]
          ? this.accountService.getAccountDetails({ accountId }).pipe(
              map(({ sites }) =>
                preloadSitesSuccess({
                  accountId,
                  sites: !!sites ? (sites as Site[]) : [],
                }),
              ),
              catchError(() => of(preloadFailure({ accountId }))),
            )
          : EMPTY,
      ),
    ),
  );

  public cleanCurrentAccountSites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clearActiveAccount),
      map(() => clearSites()),
    ),
  );

  constructor(
    private actions$: Actions,
    private merchandiseService: MerchandiseService,
    private store: Store,
    private accountService: AccountsService,
    private environmentService: EnvironmentService,
  ) {}
}
